import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi, LoginPayload } from '../../apis/auth';
import { StorageEnum } from '../../utils/enum';
import { getRoleFromToken } from '../../utils/tokenUtils';
import { clearPermissionCache } from '../../hooks/usePermission';

interface AuthState {
    user: any | null;
    role: string | null;
    loading: boolean;
    error: string | null;
}

const tokenRole = getRoleFromToken();
let parsedUser = null;
try {
    const storedUser = localStorage.getItem(StorageEnum.USER_INFO);
    if (storedUser) {
        parsedUser = JSON.parse(storedUser);
        if (parsedUser && tokenRole) {
            parsedUser.role = tokenRole;
        }
    }
} catch (e) {
    parsedUser = null;
}

const initialState: AuthState = {
    user: parsedUser,
    role: tokenRole,
    loading: false,
    error: null,
};

const setSharedCookie = (token: string | null) => {
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');
    let domainStr = '';
    if (domainParts.length > 2) {
        domainStr = `domain=.${domainParts.slice(-2).join('.')};`;
    } else if (hostname !== 'localhost') {
        domainStr = `domain=.${hostname};`;
    }
    
    if (token) {
        document.cookie = `access_token=${token}; ${domainStr} path=/; max-age=2592000;`;
    } else {
        document.cookie = `access_token=; ${domainStr} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    }
};

export const loginUser = createAsyncThunk(
    'auth/login',
    async (payload: LoginPayload, { rejectWithValue }) => {
        try {
            const response = await authApi.login(payload);
            const token = response.data.access_token;

            localStorage.removeItem(StorageEnum.PARENT_ACCESS_TOKEN);
            localStorage.setItem(StorageEnum.ACCESS_TOKEN, token);
            setSharedCookie(token);

            // Read role from server-signed JWT token (tamper-proof)
            const tokenRole = getRoleFromToken(token);

            const userResponse = await authApi.getMe();
            const user = userResponse.data;

            localStorage.setItem(StorageEnum.USER_INFO, JSON.stringify(user));
            // Sync localStorage for backward compat, but JWT is the source of truth
            localStorage.setItem(StorageEnum.USER_ROLE, tokenRole || user.role);

            sessionStorage.removeItem('teacher_welcome_dismissed');
            sessionStorage.removeItem('parent_welcome_dismissed');
            sessionStorage.removeItem('student_welcome_dismissed');

            return { ...user, role: tokenRole || user.role };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || 'Login failed');
        }
    }
);

export const loginAsChildUser = createAsyncThunk(
    'auth/loginAsChild',
    async (childId: string, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState };
            if (state.auth.role === 'PARENT') {
                const currentToken = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
                if (currentToken) {
                    localStorage.setItem(StorageEnum.PARENT_ACCESS_TOKEN, currentToken);
                }
            }

            const response = await authApi.loginAsChild(childId);
            const token = response.data.access_token;

            localStorage.setItem(StorageEnum.ACCESS_TOKEN, token);
            setSharedCookie(token);

            // Read role from server-signed JWT token (tamper-proof)
            const tokenRole = getRoleFromToken(token);

            const userResponse = await authApi.getMe();
            const user = userResponse.data;

            localStorage.setItem(StorageEnum.USER_INFO, JSON.stringify(user));
            localStorage.setItem(StorageEnum.USER_ROLE, tokenRole || user.role);

            return { ...user, role: tokenRole || user.role };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || 'Login as child failed');
        }
    }
);

export const returnToParentAccount = createAsyncThunk(
    'auth/returnToParent',
    async (_, { rejectWithValue }) => {
        try {
            const parentToken = localStorage.getItem(StorageEnum.PARENT_ACCESS_TOKEN);
            if (!parentToken) throw new Error('No parent token found');

            localStorage.setItem(StorageEnum.ACCESS_TOKEN, parentToken);
            setSharedCookie(parentToken);
            localStorage.removeItem(StorageEnum.PARENT_ACCESS_TOKEN);

            // Read role from server-signed JWT token (tamper-proof)
            const tokenRole = getRoleFromToken(parentToken);

            const userResponse = await authApi.getMe();
            const user = userResponse.data;

            localStorage.setItem(StorageEnum.USER_INFO, JSON.stringify(user));
            localStorage.setItem(StorageEnum.USER_ROLE, tokenRole || user.role);

            return { ...user, role: tokenRole || user.role };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to return to parent account');
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.role = null;
            clearPermissionCache();
            localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
            localStorage.removeItem(StorageEnum.USER_INFO);
            localStorage.removeItem(StorageEnum.USER_ROLE);
            localStorage.removeItem(StorageEnum.PARENT_ACCESS_TOKEN);
            sessionStorage.removeItem('teacher_welcome_dismissed');
            sessionStorage.removeItem('parent_welcome_dismissed');
            sessionStorage.removeItem('student_welcome_dismissed');
            setSharedCookie(null);
        },
        updateUser: (state, action) => {
            const updated = { ...state.user, ...action.payload };
            if (updated.total_credits !== undefined) {
                updated.credits = updated.total_credits;
            } else if (updated.credits !== undefined) {
                updated.total_credits = updated.credits;
            }
            state.user = updated;
            // Role is always read from the server-signed JWT token (tamper-proof)
            // Do NOT accept role from action.payload to prevent client-side tampering
            const tokenRole = getRoleFromToken();
            if (tokenRole) {
                state.role = tokenRole;
                localStorage.setItem(StorageEnum.USER_ROLE, tokenRole);
            }
            localStorage.setItem(StorageEnum.USER_INFO, JSON.stringify(state.user));
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.user = null;
                state.role = null;
                clearPermissionCache();
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                clearPermissionCache();
                const user = action.payload;
                if (user && user.total_credits !== undefined) {
                    user.credits = user.total_credits;
                }
                state.user = user;
                state.role = action.payload.role;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(loginAsChildUser.pending, (state) => {
                state.loading = true;
                state.error = null;
                clearPermissionCache();
            })
            .addCase(loginAsChildUser.fulfilled, (state, action) => {
                state.loading = false;
                clearPermissionCache();
                const user = action.payload;
                if (user && user.total_credits !== undefined) {
                    user.credits = user.total_credits;
                }
                state.user = user;
                state.role = action.payload.role;
            })
            .addCase(loginAsChildUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(returnToParentAccount.pending, (state) => {
                state.loading = true;
                state.error = null;
                clearPermissionCache();
            })
            .addCase(returnToParentAccount.fulfilled, (state, action) => {
                state.loading = false;
                clearPermissionCache();
                const user = action.payload;
                if (user && user.total_credits !== undefined) {
                    user.credits = user.total_credits;
                }
                state.user = user;
                state.role = action.payload.role;
            })
            .addCase(returnToParentAccount.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
